import {
  type CatalogoDeDatos,
  type CatalogoDeMedicamentos,
  catalogoDeMedicamentosSchema,
  type EntradaDeMedicamento,
  etiquetaDeMedicamento,
} from '@pasos/schemas';
import { useQuery } from '@tanstack/react-query';
import { obtener } from '../lib/api.js';

/**
 * Los catálogos de datos que nombra un cuestionario, descargados (E3.25).
 *
 * **La lista se guarda en el teléfono.** Es de red, sin un dato del paciente, y
 * sin ella el buscador de la droga no funciona sin señal: el check-in se puede
 * terminar offline (S-08) y el cuestionario de hito también tiene que poder. Lo
 * que se guarda es la última lista que el servidor mandó; el navegador además
 * la revalida con el `ETag` en cada pedido, así que una lista nueva de Fundación
 * llega en cuanto hay conexión.
 *
 * El almacenamiento es una comodidad y no una garantía: si el teléfono no deja
 * escribir, se sigue con la lista en memoria.
 */

const CLAVE = 'pasos.catalogo.medicamentos';

function leerGuardado(): CatalogoDeMedicamentos | undefined {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return undefined;
    const leido = catalogoDeMedicamentosSchema.safeParse(JSON.parse(crudo));
    return leido.success ? leido.data : undefined;
  } catch {
    return undefined;
  }
}

function guardar(catalogo: CatalogoDeMedicamentos): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(catalogo));
  } catch {
    // Sin almacenamiento, la lista sigue en memoria mientras dure la pantalla.
  }
}

export async function descargarMedicamentos(): Promise<CatalogoDeMedicamentos> {
  const catalogo = catalogoDeMedicamentosSchema.parse(
    await obtener<unknown>('/api/v1/catalogos/medicamentos'),
  );
  guardar(catalogo);
  return catalogo;
}

/**
 * El catálogo de medicamentos. `habilitado` en falso no pide nada: la mayoría de
 * los formularios no nombra ningún catálogo y no tiene por qué pagar el pedido.
 */
export function useCatalogoDeMedicamentos(habilitado: boolean) {
  return useQuery({
    queryKey: ['catalogos', 'medicamentos'],
    queryFn: descargarMedicamentos,
    enabled: habilitado,
    initialData: leerGuardado,
    // La copia guardada se muestra al instante y se revalida igual.
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60_000,
  });
}

/** Lo que las piezas del formulario necesitan de los catálogos: sus entradas. */
export type CatalogosDescargados = Readonly<
  Partial<Record<CatalogoDeDatos, readonly EntradaDeMedicamento[]>>
>;

/** Cómo se llama una entrada, o `undefined` si la lista todavía no llegó. */
export function etiquetaDeLaEntrada(
  catalogos: CatalogosDescargados,
  catalogo: CatalogoDeDatos,
  codigo: string,
): string | undefined {
  const entrada = catalogos[catalogo]?.find((candidata) => candidata.codigo === codigo);
  return entrada ? etiquetaDeMedicamento(entrada) : undefined;
}
